import type { ReactNode } from "react";

import { PageHeader } from "@/shell/PageHeader";

/**
 * The shared Clinical library's section header: breadcrumb, title, description
 * and the section's own top-right action area (#908).
 *
 * ## WHAT IT REPLACES, AND WHY THE SHELL'S OWN HEADER HAD TO GO
 *
 * `LibraryRoute` used to render ONE `PageHeader` — `h1` "Clinical library" over
 * a paragraph about pinned adoption — above the rail, for all seven sections.
 * The reference (`/library/pathways`, `/library/bg-sets`, `/library/summary`)
 * carries no such banner: the rail is persistent, and the content area opens
 * with `Clinical library / <Section>`, the SECTION's name as the page's only
 * `h1`, the section's own description, and that section's creation action at
 * the top right of the same row. A clinician therefore reads which family they
 * are in from the heading rather than from the rail's selected item alone.
 *
 * `NamedSectionHeader` (#492) stood in for half of this on the two canonical
 * catalogs. It rendered an `h2` deliberately, because the shell's `h1` was
 * still above it and two `h1`s would have left the document with competing
 * top-level headings — and it recorded that reconciling the two into the
 * canonical single one was a later ticket's. This is that reconciliation: the
 * shell's header is gone, so the section's heading is the page's `h1`, and
 * every library section gets one from the same component instead of two of the
 * seven getting one of their own.
 *
 * ## WHY THE ACTIONS ARE A SLOT AND NOT DERIVED HERE
 *
 * What a section can create — and whether the session may — is the catalog's
 * question, not the header's: `library.write`'s refusal text, the governed
 * families the placement registry names, and which typed dialog a control
 * opens all live with the rows. So this component renders the area and the
 * catalog fills it. That is what lets the BG, Rules and Summary leaves put
 * their own creation controls in the same place without each inventing a
 * header, and it is why `actions` is `ReactNode` rather than a description of
 * a button.
 *
 * ## THE BREADCRUMB'S PARENT IS BARE `/library`
 *
 * Deliberately, and it is the one place that address is correct: it is UP
 * navigation to the library as a whole, exactly as `PathwayAssetPage` and
 * `MainConcernAssetPage` already use it. `src/invariants/projectUxAmendment.ts`
 * forbids bare `/library` on the ADOPTION surfaces, where it would land a
 * clinician on whichever section the router lists first instead of the family
 * they were working in; a breadcrumb's parent crumb is the opposite case.
 */
export function LibrarySectionHeader({
  sectionLabel,
  description,
  actions,
}: {
  /** The section's product name — "Pathways", "BG sets", "Summary". */
  sectionLabel: string;
  description: string;
  /** The section's creation and view controls, rendered at the top right. */
  actions?: ReactNode;
}) {
  return (
    <PageHeader
      breadcrumbs={[
        { label: "Clinical library", to: "/library" },
        { label: sectionLabel },
      ]}
      title={sectionLabel}
      description={description}
      {...(actions ? { actions } : {})}
    />
  );
}
